// What the GST and compliance page reads (#486): `GET /api/gst`, for a period's month, and where each export it lists
// downloads from. Amounts are in paise.
import { call, type Fetch } from "../http.js";

export interface Heads {
  readonly igst: number;
  readonly cgst: number;
  readonly sgst: number;
}
export type TaxedHeads = Heads & { readonly taxableValue: number };

export interface GstPosition {
  readonly status: "shown";
  readonly period: string;
  readonly periodLabel: string;
  readonly filing: { readonly id: string; readonly status: string } | null;
  readonly figures: {
    readonly outward: TaxedHeads;
    readonly reverseCharge: TaxedHeads;
    readonly itcReverseCharge: Heads;
    readonly itcOthers: Heads;
    readonly itcNet: Heads;
    readonly cash: Heads;
    readonly carriedForward: Heads;
  };
  /** The GSTR-3B version the figures are, and whether today's postings differ; null when they are today's. */
  readonly kept: { readonly version: number; readonly issuedAt: string; readonly changed: boolean } | null;
  readonly totals: { readonly output: number; readonly itc: number; readonly net: number; readonly carried: number };
  readonly recorded: boolean;
  readonly notes: readonly string[];
}

/** before_lamp: filed before the business came to LAMP, as a person said (#782). */
export type FilingStatus = "due" | "done" | "late" | "before_lamp";

export interface CalendarEntry {
  readonly id: string | null;
  readonly obligation: string;
  readonly name: string;
  readonly period: string;
  readonly periodLabel: string;
  readonly dueDate: string;
  readonly status: FilingStatus;
  readonly daysLate: number | null;
  readonly filedOn: string | null;
}

export type ExportKind = "gstr1" | "gstr3b" | "tds_salary" | "tds_non_salary";

export interface ExportView {
  readonly filing: string;
  readonly kind: ExportKind;
  readonly name: string;
  readonly periodLabel: string;
  readonly status: FilingStatus;
  readonly daysLate: number | null;
  readonly filedOn: string | null;
  readonly shown: boolean;
  readonly versions?: readonly {
    readonly version: number;
    readonly issuedAt: string;
    readonly files: readonly { readonly part: string; readonly name: string }[];
  }[];
  readonly problems?: readonly string[];
}

export interface GstPage {
  readonly month: string;
  readonly periodLabel: string;
  readonly position: GstPosition | { readonly status: "failed"; readonly problems: readonly string[] };
  readonly calendar:
    | {
        readonly status: "shown";
        readonly month: string;
        readonly cycle: "on" | "off";
        readonly filings: readonly CalendarEntry[];
        readonly problems: readonly string[];
      }
    | { readonly status: "failed"; readonly problems: readonly string[] };
  readonly exports: readonly ExportView[];
  readonly tds: readonly ExportView[];
}

export interface GstApi {
  /** The page for the period with `month` in it; last month's unless given. */
  gst(month?: string): Promise<GstPage>;
}

export const createGstApi = (fetcher: Fetch = (...args) => fetch(...args)): GstApi => ({
  gst: (month) => call<GstPage>(fetcher, month ? `/api/gst?month=${encodeURIComponent(month)}` : "/api/gst"),
});

/** Where one file of an export's version downloads from, by the route that serves its kind. */
export function exportHref(view: Pick<ExportView, "filing" | "kind">, version: number, part: string): string {
  const { filing } = view;
  if (view.kind === "gstr1") return `/api/compliance/filings/${filing}/gstr1/${String(version)}`;
  if (view.kind === "gstr3b") return `/api/compliance/filings/${filing}/gstr3b/${String(version)}/${part}`;
  return `/api/compliance/filings/${filing}/tds-return/${String(version)}/${part}`;
}
